import { TypingDots } from './TypingDots';
import { CheckIcon } from './Icons';
import { T } from '../lib/design-tokens';
import type { TaskState } from '../types';

interface TimelineEvent {
  timestamp: string;
  event: string;
  details?: string;
}

interface TimelineViewProps {
  events: TimelineEvent[];
  currentState: TaskState;
}

const TERMINAL_STATES: TaskState[] = ['COMPLETED', 'FAILED', 'CANCELLED', 'NO_RESPONSE'] as unknown as TaskState[];

function isTerminal(state: TaskState) {
  return TERMINAL_STATES.includes(state);
}

function timeAgo(ts: string) {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function TimelineView({ events, currentState }: TimelineViewProps) {
  if (!events.length) return null;

  const terminal = isTerminal(currentState);
  const statuses: ('done' | 'current' | 'pending')[] = events.map(() => 'pending');

  events.forEach((ev, i) => {
    if (ev.timestamp && new Date(ev.timestamp).getTime() <= Date.now()) {
      statuses[i] = 'done';
    }
  });

  if (!terminal && (currentState === 'EXECUTING' || currentState === 'APPROVED')) {
    const lastDone = statuses.lastIndexOf('done');
    if (lastDone >= 0 && lastDone === events.length - 1) {
      statuses[lastDone] = 'current';
    } else if (lastDone >= 0 && lastDone < events.length - 1) {
      statuses[lastDone + 1] = 'current';
    } else {
      statuses[0] = 'current';
    }
  }

  return (
    <div className="relative" style={{ paddingLeft: 20 }}>
      <div
        className="absolute"
        style={{
          left: 15,
          top: 12,
          bottom: 12,
          width: 1,
          background: T.cream08,
        }}
      />

      {events.map((ev, i) => {
        const status = statuses[i];
        return (
          <div
            key={i}
            className="relative flex items-start gap-4"
            style={{
              padding: '14px 0',
              animation: `fadeUp 0.4s ease ${i * 0.1}s both`,
            }}
          >
            <div
              className="shrink-0 flex items-center justify-center rounded-full relative z-[1]"
              style={{
                width: 32,
                height: 32,
                marginLeft: -20,
                background:
                  status === 'done'
                    ? T.green
                    : status === 'current'
                      ? T.goldGlow
                      : T.surface,
                border:
                  status === 'done'
                    ? 'none'
                    : status === 'current'
                      ? `2px solid ${T.gold}`
                      : `1.5px solid ${T.cream12}`,
                color: status === 'done' ? T.bg : status === 'current' ? T.gold : T.cream20,
                boxShadow: status === 'current' ? `0 0 0 4px ${T.goldGlow}` : 'none',
                transition: 'all 0.3s',
              }}
            >
              {status === 'done' && <CheckIcon size={14} />}
              {status === 'current' && (
                <span
                  className="rounded-full"
                  style={{
                    width: 7,
                    height: 7,
                    background: T.gold,
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }}
                />
              )}
              {status === 'pending' && (
                <span style={{ fontFamily: T.mono, fontSize: 11, fontWeight: 700 }}>
                  {i + 1}
                </span>
              )}
            </div>

            <div className="flex-1 min-w-0 pt-1">
              <div className="flex items-center gap-2">
                <span
                  style={{
                    fontFamily: T.sans,
                    fontSize: 14,
                    fontWeight: 500,
                    color: status === 'done' ? T.cream35 : T.cream,
                  }}
                >
                  {ev.event}
                </span>
                {status === 'current' && <TypingDots />}
              </div>
              {ev.details && (
                <div style={{ fontFamily: T.sans, marginTop: 2, fontSize: 12, color: T.cream20, marginLeft: 22 }}>
                  {ev.details}
                </div>
              )}
              {ev.timestamp && (
                <span style={{ fontFamily: T.mono, fontSize: 10, color: T.cream12, marginLeft: 22 }}>
                  {timeAgo(ev.timestamp)}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
